/**
 * What a programme list filters on: the options, and the predicates.
 *
 * The controls this once drew are `programme-filter-defs.ts`
 * handed to `useCollectionFilters`, which draws the panel.
 *
 * @module components/shared/programme-filter-rules
 */

import type { ProgrammeSummary } from "@/platform";

import { isRetired } from "./programme-retirement";

export type ProgrammeLevelFilter = "all" | "unspecified" | `${number}`;
export type ProgrammeWorkflowFilter =
  | "all"
  | "draft"
  | "ready-for-development"
  | "in-development"
  | "internal-review"
  | "awaiting-qa-approval"
  | "approved"
  | "exported-and-locked"
  | "active-delivery"
  | "retired-delivery";

export const UNSPECIFIED_DISCIPLINE_FILTER = "__unspecified__";

export const WORKFLOW_OPTIONS: readonly { value: ProgrammeWorkflowFilter; label: string }[] = [
  { value: "all", label: "All states" },
  { value: "draft", label: "Draft" },
  { value: "ready-for-development", label: "Ready for Development" },
  { value: "in-development", label: "In Development" },
  { value: "internal-review", label: "Internal Review" },
  { value: "awaiting-qa-approval", label: "Awaiting QA Approval" },
  { value: "approved", label: "Approved" },
  { value: "exported-and-locked", label: "Exported and Locked" },
  { value: "active-delivery", label: "Active delivery" },
  { value: "retired-delivery", label: "Retired delivery" },
];

export function matchesWorkflow(
  programme: ProgrammeSummary,
  filter: ProgrammeWorkflowFilter,
): boolean {
  if (filter === "all") {
    return true;
  }
  const workflow = programme.workflowState.toLocaleLowerCase();
  const delivery = (programme.deliveryStatus ?? "").toLocaleLowerCase();
  switch (filter) {
    case "draft":
      return workflow.includes("draft") || workflow.includes("sandbox");
    case "ready-for-development":
      return workflow.includes("ready for development");
    case "in-development":
      return workflow.includes("in development");
    case "internal-review":
      return workflow.includes("internal review");
    case "awaiting-qa-approval":
      return workflow.includes("awaiting qa approval");
    case "approved":
      return workflow.includes("approved") && !workflow.includes("awaiting");
    case "exported-and-locked":
      return workflow.includes("exported") || workflow.includes("locked");
    case "active-delivery":
      return delivery === "active" && !isRetired(programme);
    case "retired-delivery":
      return isRetired(programme);
    default: {
      // Unreachable: every member of the union has a case above, and this
      // assignment is what makes tsc say so if one is ever added without one.
      const unhandled: never = filter;
      throw new Error(`Unhandled filter: ${String(unhandled)}`);
    }
  }
}

export function matchesText(
  programme: ProgrammeSummary,
  query: string,
  discipline: string,
): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) {
    return true;
  }
  return [
    programme.title,
    programme.awardType,
    programme.workflowState,
    programme.deliveryStatus ?? "",
    programme.nfqLevel === null ? "" : `nfq ${programme.nfqLevel}`,
    `revision ${programme.saveRevision}`,
    discipline,
  ].some((value) => value.toLocaleLowerCase().includes(normalized));
}
